import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Info, Warning, Wrench, CheckCircle, ArrowRight } from '@phosphor-icons/react';
import axios from 'axios';

import { API_URL } from '../config/api';

/**
 * AnnouncementBanner - Displays system-wide announcements
 * Types: info, warning, maintenance, success
 * Dismissible announcements are remembered in localStorage for 24hrs
 *
 * Kiosk routes (e.g. /console) suppress the banner entirely so the
 * wall-mounted dashboard stays dark edge-to-edge.
 */
const KIOSK_PATHS = ['/console', '/widget'];

export const AnnouncementBanner = () => {
  const location = useLocation();
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedIds, setDismissedIds] = useState([]);

  useEffect(() => {
    fetchAnnouncements();
    loadDismissedFromStorage();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/announcements`);
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    }
  };

  const loadDismissedFromStorage = () => {
    try {
      const stored = localStorage.getItem('turfpulse_dismissed_announcements');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Filter out expired dismissals (older than 24hrs)
        const now = Date.now();
        const validDismissals = Object.entries(parsed)
          .filter(([_, timestamp]) => now - timestamp < 24 * 60 * 60 * 1000)
          .map(([id]) => id);
        setDismissedIds(validDismissals);
      }
    } catch (e) {
      console.error('Error loading dismissed announcements:', e);
    }
  };

  const handleDismiss = (announcementId) => {
    // Add to dismissed list
    setDismissedIds(prev => [...prev, announcementId]);
    
    // Store in localStorage with timestamp
    try {
      const stored = JSON.parse(localStorage.getItem('turfpulse_dismissed_announcements') || '{}');
      stored[announcementId] = Date.now();
      localStorage.setItem('turfpulse_dismissed_announcements', JSON.stringify(stored));
    } catch (e) {
      console.error('Error saving dismissed announcement:', e);
    }
  };

  const getTypeConfig = (type) => {
    switch (type) {
      case 'warning':
        return {
          bg: 'bg-amber-500',
          text: 'text-amber-950',
          icon: <Warning weight="bold" size={20} aria-hidden="true" />,
          border: 'border-amber-600'
        };
      case 'maintenance':
        return {
          bg: 'bg-blue-600',
          text: 'text-white',
          icon: <Wrench weight="bold" size={20} aria-hidden="true" />,
          border: 'border-blue-700'
        };
      case 'success':
        return {
          bg: 'bg-green-500',
          text: 'text-green-950',
          icon: <CheckCircle weight="bold" size={20} aria-hidden="true" />,
          border: 'border-green-600'
        };
      case 'info':
      default:
        return {
          bg: 'bg-primary',
          text: 'text-primary-foreground',
          icon: <Info weight="bold" size={20} aria-hidden="true" />,
          border: 'border-primary/80'
        };
    }
  };

  // Filter out dismissed announcements
  const visibleAnnouncements = announcements.filter(
    ann => !dismissedIds.includes(ann.id)
  );

  // Kiosk routes render nothing so the wall dashboard stays dark.
  if (KIOSK_PATHS.includes(location.pathname)) {
    return null;
  }

  if (visibleAnnouncements.length === 0) {
    return null;
  }

  return (
    <div className="announcement-banners" role="region" aria-label="System announcements">
      {visibleAnnouncements.map((announcement) => {
        const config = getTypeConfig(announcement.type);
        
        return (
          <div
            key={announcement.id}
            data-testid={`announcement-${announcement.id}`}
            className={`${config.bg} ${config.text} py-2.5 px-4 relative`}
            role="alert"
            aria-live="polite"
          >
            <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 text-sm font-medium">
              <span className="flex-shrink-0">{config.icon}</span>
              <div className="flex items-center gap-2 flex-wrap justify-center">
                {announcement.title && (
                  <span className="font-bold">{announcement.title}:</span>
                )}
                <span>{announcement.message}</span>
                {announcement.link_url && (
                  <a
                    href={announcement.link_url}
                    className="inline-flex items-center gap-1 underline underline-offset-2 hover:opacity-80 transition-opacity"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {announcement.link_text || 'Learn more'}
                    <ArrowRight weight="bold" size={14} aria-hidden="true" />
                  </a>
                )}
              </div>
              {announcement.dismissible && (
                <button
                  onClick={() => handleDismiss(announcement.id)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:opacity-70 transition-opacity rounded"
                  aria-label="Dismiss announcement"
                  data-testid={`dismiss-${announcement.id}`}
                >
                  <X weight="bold" size={18} aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
